import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  useReactTable,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/Table';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ChevronDown, ChevronUp, ChevronsUpDown, SearchX } from 'lucide-react';
import { cn } from '../../lib/utils';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchKey?: string;
  searchPlaceholder?: string;
  /** Renders in the toolbar row next to search (e.g. filters). */
  toolbarExtra?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  /** Accessible name for the table. */
  label?: string;
  /** Shown when there are no rows at all, or none match the search. */
  emptyMessage?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = 'Search...',
  toolbarExtra,
  onRowClick,
  label = 'Results',
  emptyMessage = 'No results match your search.',
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const rowCount = table.getFilteredRowModel().rows.length;

  return (
    <div className="space-y-4">
      {/* Search Input */}
      {(searchKey || toolbarExtra) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {searchKey ? (
            <Input
              type="search"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={(table.getColumn(searchKey)?.getFilterValue() as string) ?? ''}
              onChange={(event) =>
                table.getColumn(searchKey)?.setFilterValue(event.target.value)
              }
              className="w-full sm:max-w-sm"
            />
          ) : (
            <div />
          )}
          {toolbarExtra ? <div className="flex flex-wrap items-center gap-2">{toolbarExtra}</div> : null}
        </div>
      )}

      {/* Phones: each row becomes a card so every column and action stays within reach. */}
      <ul className="space-y-3 md:hidden" aria-label={label}>
        {table.getRowModel().rows.length ? (
          table.getRowModel().rows.map((row) => (
            <li
              key={row.id}
              className={cn(
                'space-y-3 rounded-md border border-gray-200 bg-white p-4',
                onRowClick && 'cursor-pointer active:bg-muted/50'
              )}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            >
              {row.getVisibleCells().map((cell) => {
                const header = cell.column.columnDef.header;
                const heading = typeof header === 'string' && header.trim() ? header : null;
                return (
                  <div key={cell.id} className="min-w-0 space-y-1 break-words">
                    {heading ? (
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{heading}</p>
                    ) : null}
                    <div className="text-sm">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                  </div>
                );
              })}
            </li>
          ))
        ) : (
          <li className="flex flex-col items-center justify-center gap-2 rounded-md border border-gray-200 bg-white p-8 text-center">
            <SearchX className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          </li>
        )}
      </ul>

      {/* Table */}
      <div className="hidden rounded-md border border-gray-200 bg-white md:block">
        <Table aria-label={label}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      aria-sort={
                        !header.column.getCanSort()
                          ? undefined
                          : sorted === 'asc'
                            ? 'ascending'
                            : sorted === 'desc'
                              ? 'descending'
                              : 'none'
                      }
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="-mx-2 inline-flex select-none items-center gap-2 rounded-md px-2 py-1 font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {sorted === 'asc' ? (
                            <ChevronUp className="h-4 w-4" aria-hidden="true" />
                          ) : sorted === 'desc' ? (
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <ChevronsUpDown
                              className="h-4 w-4 opacity-50"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    onRowClick &&
                      'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
                  )}
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-32">
                  <div className="flex flex-col items-center justify-center gap-2 text-center">
                    <SearchX
                      className="h-5 w-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-muted-foreground">{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {table.getPageCount() > 1 && (
        <nav
          className="flex flex-wrap items-center justify-between gap-3"
          aria-label={`${label} pagination`}
        >
          <p className="text-sm text-muted-foreground" aria-live="polite">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
            <span className="hidden sm:inline">
              {' '}
              &middot; {rowCount} {rowCount === 1 ? 'result' : 'results'}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
}
